import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { TabsProvider } from "@/providers/tabs-provider";

const SHOW_TABS_KEY = "ehr_show_tabs";
const COPILOT_KEY = "ehr_copilot_enabled";

interface WorkbenchContextValue {
  showTabs: boolean;
  setShowTabs: (value: boolean) => void;
  toggleTabs: () => void;
  copilotEnabled: boolean;
  setCopilotEnabled: (value: boolean) => void;
  toggleCopilot: () => void;
}

const WorkbenchContext = createContext<WorkbenchContextValue | null>(null);

export function useWorkbench() {
  const ctx = useContext(WorkbenchContext);
  if (!ctx) {
    throw new Error("useWorkbench must be used within WorkbenchProvider");
  }
  return ctx;
}

export function WorkbenchProvider({ children }: { children: ReactNode }) {
  const [showTabs, setShowTabsState] = useState<boolean>(() => {
    return localStorage.getItem(SHOW_TABS_KEY) !== "false";
  });
  const [copilotEnabled, setCopilotEnabledState] = useState<boolean>(() => {
    return localStorage.getItem(COPILOT_KEY) !== "false";
  });

  const setShowTabs = useCallback((value: boolean) => {
    setShowTabsState(value);
    localStorage.setItem(SHOW_TABS_KEY, String(value));
  }, []);

  const toggleTabs = useCallback(() => {
    setShowTabs(!showTabs);
  }, [setShowTabs, showTabs]);

  const setCopilotEnabled = useCallback((value: boolean) => {
    setCopilotEnabledState(value);
    localStorage.setItem(COPILOT_KEY, String(value));
  }, []);

  const toggleCopilot = useCallback(() => {
    setCopilotEnabled(!copilotEnabled);
  }, [copilotEnabled, setCopilotEnabled]);

  return (
    <WorkbenchContext.Provider
      value={{
        showTabs,
        setShowTabs,
        toggleTabs,
        copilotEnabled,
        setCopilotEnabled,
        toggleCopilot,
      }}
    >
      <TabsProvider>{children}</TabsProvider>
    </WorkbenchContext.Provider>
  );
}
